import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AdminApiService } from '../admin-api.service';
import { SystemApp } from '../models';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  standalone: true,
  selector: 'app-systems-page',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputSwitchModule,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['./systems.page.scss'],
  templateUrl: './systems.page.html',
})
export class SystemsPage implements OnInit {
  api = inject(AdminApiService);
  ms = inject(MessageService);
  confirmService = inject(ConfirmationService);

  rows: SystemApp[] = [];
  loading = false;

  show = false;
  editId: string | null = null;
  saving = false;
  form: SystemApp = { key: '', displayName: '', isActive: true };

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading = true;
    this.api.listSystems().subscribe({
      next: (d) => {
        this.rows = d || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading systems:', err);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل الأنظمة' });
        this.rows = [];
        this.loading = false;
      },
    });
  }

  openNew() {
    this.editId = null;
    this.form = { key: '', displayName: '', isActive: true };
    this.show = true;
  }

  openEdit(row: SystemApp) {
    this.editId = row.id || null;
    this.form = {
      id: row.id,
      key: row.key,
      displayName: row.displayName,
      isActive: row.isActive !== false,
    };
    this.show = true;
  }

  save() {
    const key = (this.form.key || '').trim();
    const displayName = (this.form.displayName || '').trim();

    if (!key) {
      this.ms.add({ severity: 'warn', summary: 'تحذير', detail: 'الـ Key مطلوب' });
      return;
    }
    if (!displayName) {
      this.ms.add({ severity: 'warn', summary: 'تحذير', detail: 'الاسم الظاهر مطلوب' });
      return;
    }

    const body: SystemApp = {
      ...(this.editId ? { id: this.editId } : {}),
      key,
      displayName,
      isActive: this.form.isActive !== false,
    };

    this.saving = true;
    const req$ = this.editId ? this.api.updateSystem(this.editId, body) : this.api.createSystem(body);
    req$.subscribe({
      next: () => {
        this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم الحفظ' });
        this.show = false;
        this.saving = false;
        this.load();
      },
      error: (err) => {
        const msg = err?.error?.message || err?.error || 'تعذر الحفظ';
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: typeof msg === 'string' ? msg : 'تعذر الحفظ' });
        this.saving = false;
      },
    });
  }

  remove(row: SystemApp) {
    if (!row.id) return;
    this.confirmService.confirm({
      message: `هل أنت متأكد من حذف النظام «${row.displayName}»؟`,
      header: 'تأكيد الحذف',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.api.deleteSystem(row.id!).subscribe({
          next: () => {
            this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم الحذف' });
            this.load();
          },
          error: (err) => {
            const msg = err?.error?.message || err?.error || 'تعذر الحذف';
            this.ms.add({ severity: 'error', summary: 'خطأ', detail: typeof msg === 'string' ? msg : 'تعذر الحذف' });
          },
        });
      },
    });
  }
}
